import dayjs, { Dayjs } from 'dayjs';
import moment, { Moment } from 'moment';

import { ONE_MILLISECOND, TIME_FORMAT } from '@/components/ui/Timeline/utils/constants';
import type { CycleCallback, CycleData } from '@/components/ui/Timeline';
import { roundToMilliseconds } from '@/components/ui/Timeline/utils/helpers/cycle-helpers';
import {
	getDurationFromInterval,
	getNextValidStart,
	toVisualEndTime
} from '@/components/ui/Timeline/utils/interval-helpers';
import { dayjsToSeconds, secondsToDayjs } from '@/components/ui/Timeline/utils/time-conversions';
import { isOutOfVideoRange } from '@/components/views/BeraJob/BeraReportV2/coverage';

const DEFAULT_STRETCH_DURATION_IN_SECONDS = 2;
const MINIMUM_STRETCH_DURATION_IN_SECONDS = 1;
const PICKER_PANEL_HEIGHT_IN_PIXELS = 280;
const SECONDS_IN_A_DAY = 86400;
const HOURS_IN_A_DAY = 24;
const MINUTES_IN_AN_HOUR = 60;

export interface StretchRangeInSeconds {
	start: number;
	end: number;
}

export interface PickerBounds {
	disabledHours: () => number[];
	disabledMinutes: (hour: number) => number[];
	disabledSeconds: (hour: number, minute: number) => number[];
}

function unitsFrom(first: number, limit: number): number[] {
	if (first >= limit) {
		return [];
	}

	const result = Array.from({ length: limit - first }, (_, index) => first + index);

	return result;
}

export function toMoment(value: Dayjs): Moment {
	const result = moment(value.toDate());

	return result;
}

export function toDayjs(value: Moment): Dayjs {
	const result = dayjs(value.toDate());

	return result;
}

function toWholeSeconds(time: Dayjs): number {
	const seconds = dayjsToSeconds(time);
	const result = Math.floor(seconds);

	return result;
}

export function getDisplayedDurationInSeconds(stretch: CycleCallback): number {
	const start = toWholeSeconds(stretch.start_time);
	const end = toWholeSeconds(stretch.end_time);
	const result = end - start;

	return result;
}

function toCycleData(stretches: CycleCallback[]): CycleData[] {
	const result = stretches.map((stretch) => ({
		id: stretch.id,
		start: dayjsToSeconds(stretch.start_time),
		end: dayjsToSeconds(stretch.end_time),
		identified: !!stretch.identified
	}));

	return result;
}

interface FreeRangeScan {
	ranges: StretchRangeInSeconds[];
	reached: number;
}

function collectFreeRange(scan: FreeRangeScan, cycle: CycleData): FreeRangeScan {
	const reached = Math.max(scan.reached, getNextValidStart(cycle.end));

	if (cycle.start <= scan.reached) {
		return { ranges: scan.ranges, reached };
	}

	const gap = { start: scan.reached, end: roundToMilliseconds(cycle.start - ONE_MILLISECOND) };
	const ranges = scan.ranges.concat(gap);

	return { ranges, reached };
}

function getRangeDuration(range: StretchRangeInSeconds): number {
	const result = getDurationFromInterval(range.start, range.end);

	return result;
}

function findFreeRanges(cycles: CycleData[], videoDuration: number): StretchRangeInSeconds[] {
	const sortedCycles = [...cycles].sort((first, second) => first.start - second.start);
	const emptyScan: FreeRangeScan = { ranges: [], reached: 0 };
	const scan = sortedCycles.reduce(collectFreeRange, emptyScan);
	const lastPlayableSecond = roundToMilliseconds(videoDuration - ONE_MILLISECOND);

	if (scan.reached > lastPlayableSecond) {
		return scan.ranges;
	}

	const tail = { start: scan.reached, end: lastPlayableSecond };
	const result = scan.ranges.concat(tail);

	return result;
}

export function findNextFreeSlot(stretches: CycleCallback[], videoDuration: number): StretchRangeInSeconds | null {
	if (videoDuration <= 0) {
		return null;
	}

	const cycles = toCycleData(stretches);
	const freeRanges = findFreeRanges(cycles, videoDuration);
	const space = freeRanges.find((range) => getRangeDuration(range) >= MINIMUM_STRETCH_DURATION_IN_SECONDS);

	if (!space) {
		return null;
	}

	const availableDuration = getRangeDuration(space);
	const duration = Math.min(availableDuration, DEFAULT_STRETCH_DURATION_IN_SECONDS);
	const result = { start: space.start, end: toVisualEndTime(space.start, duration) };

	return result;
}

interface BuildStretchParams {
	slot: StretchRangeInSeconds;
	tlidId?: string;
}

export function buildStretch({ slot, tlidId }: BuildStretchParams): CycleCallback {
	const result: CycleCallback = {
		id: crypto.randomUUID(),
		type_id: tlidId,
		start_time: secondsToDayjs(slot.start),
		end_time: secondsToDayjs(slot.end),
		identified: false
	};

	return result;
}

function getLastPlayableSecond(videoDuration: number): number {
	const result = Math.min(videoDuration, SECONDS_IN_A_DAY);

	return result;
}

export function isWithinVideo(stretch: CycleCallback, videoDuration: number): boolean {
	const end = dayjsToSeconds(stretch.end_time);
	const lastPlayableSecond = getLastPlayableSecond(videoDuration);
	const result = !isOutOfVideoRange(end, lastPlayableSecond);

	return result;
}

export function clampStretchToVideo(stretch: CycleCallback, videoDuration: number): CycleCallback | null {
	if (isWithinVideo(stretch, videoDuration)) {
		return stretch;
	}

	const lastPlayableSecond = getLastPlayableSecond(videoDuration);
	const start = dayjsToSeconds(stretch.start_time);
	const remainingDuration = lastPlayableSecond - start;

	if (remainingDuration < MINIMUM_STRETCH_DURATION_IN_SECONDS) {
		return null;
	}

	const result = { ...stretch, end_time: secondsToDayjs(lastPlayableSecond) };

	return result;
}

interface FindStretchIdsPastVideoParams {
	stretches: CycleCallback[];
	videoDuration: number;
}

export function findStretchIdsPastVideo({ stretches, videoDuration }: FindStretchIdsPastVideoParams): string[] {
	const pastVideo = stretches.filter((stretch) => {
		const isPastVideo = isOutOfVideoRange(dayjsToSeconds(stretch.end_time), videoDuration);

		return isPastVideo;
	});
	const result = pastVideo.map((stretch) => stretch.id);

	return result;
}

export function sortStretchesChronologically(stretches: CycleCallback[]): CycleCallback[] {
	const result = [...stretches].sort((first, second) => first.start_time.valueOf() - second.start_time.valueOf());

	return result;
}

export function keyStretchesById(stretches: CycleCallback[]): Record<string, CycleCallback> {
	const entries = stretches.map((stretch) => [stretch.id, stretch] as const);
	const result = Object.fromEntries(entries);

	return result;
}

export function hasStretchMoved(stretch: CycleCallback, saved?: CycleCallback): boolean {
	if (!saved) {
		return true;
	}

	const movedStart = !stretch.start_time.isSame(saved.start_time);
	const movedEnd = !stretch.end_time.isSame(saved.end_time);
	const result = movedStart || movedEnd;

	return result;
}

export function toTaskIds(stretches: CycleCallback[]): string[] {
	const taskIds = stretches.map((stretch) => stretch.type_id).filter((taskId): taskId is string => !!taskId);
	const result = Array.from(new Set(taskIds));

	return result;
}

export function findOverlappingStretches(stretches: CycleCallback[]): StretchRangeInSeconds[] | null {
	const ranges = stretches.map((stretch) => ({
		start: dayjsToSeconds(stretch.start_time),
		end: dayjsToSeconds(stretch.end_time)
	}));
	const sorted = ranges.sort((first, second) => first.start - second.start);
	const index = sorted.findIndex((range, position) => position > 0 && range.start < sorted[position - 1].end);

	if (index < 0) {
		return null;
	}

	const result = [sorted[index - 1], sorted[index]];

	return result;
}

export function buildPickerBounds(videoDuration: number): PickerBounds {
	const lastHour = Math.floor(videoDuration / TIME_FORMAT.SECONDS_PER_HOUR);
	const lastMinute = Math.floor((videoDuration % TIME_FORMAT.SECONDS_PER_HOUR) / TIME_FORMAT.SECONDS_PER_MINUTE);
	const lastSecond = Math.floor(videoDuration % TIME_FORMAT.SECONDS_PER_MINUTE);

	function disabledHours(): number[] {
		const result = unitsFrom(lastHour + 1, HOURS_IN_A_DAY);

		return result;
	}

	function disabledMinutes(hour: number): number[] {
		if (hour < lastHour) {
			return [];
		}

		const result = unitsFrom(lastMinute + 1, MINUTES_IN_AN_HOUR);

		return result;
	}

	function disabledSeconds(hour: number, minute: number): number[] {
		if (hour < lastHour || minute < lastMinute) {
			return [];
		}

		const result = unitsFrom(lastSecond + 1, TIME_FORMAT.SECONDS_PER_MINUTE);

		return result;
	}

	const result = { disabledHours, disabledMinutes, disabledSeconds };

	return result;
}

export type PickerPlacement = 'bottomLeft' | 'topLeft';

interface ResolvePickerPlacementParams {
	anchorTop: number;
	anchorBottom: number;
	viewportHeight: number;
}

export function resolvePickerPlacement({
	anchorTop,
	anchorBottom,
	viewportHeight
}: ResolvePickerPlacementParams): PickerPlacement {
	const spaceBelow = viewportHeight - anchorBottom;
	const fitsBelow = spaceBelow >= PICKER_PANEL_HEIGHT_IN_PIXELS;

	if (fitsBelow || anchorTop <= spaceBelow) {
		return 'bottomLeft';
	}

	return 'topLeft';
}
